// Render-on-demand policy of the performance budget (report section 4.3):
// up to 60 frames per second while the player touches or the camera moves,
// at most 30 when only ambient animations run, and none at all when nothing changes.

export type FrameMode = 'idle' | 'ambient' | 'interactive';

export interface FrameSchedulerOptions {
  /** Requests a callback on the next display frame (requestAnimationFrame in the browser). */
  readonly requestFrame: (callback: (time: number) => void) => void;
  /** Called on each frame that must be drawn, with the milliseconds since the previous drawn frame. */
  readonly render: (time: number, delta: number) => void;
  readonly interactiveFps?: number;
  readonly ambientFps?: number;
  /**
   * True while the GPU is still drawing the previous frame. A due frame then waits for the next
   * display callback instead of piling up behind it: a GPU-bound device (or software WebGL) keeps
   * a queue of at most one frame, so touches and the main thread never wait behind a backlog.
   */
  readonly gpuBusy?: () => boolean;
}

/** Longest gap between display callbacks that still counts as a refresh interval, in ms. */
const MAX_REFRESH_MS = 50;

export class FrameScheduler {
  private readonly requestFrame: FrameSchedulerOptions['requestFrame'];
  private readonly renderFrame: FrameSchedulerOptions['render'];
  private readonly gpuBusy: () => boolean;
  private readonly interactiveInterval: number;
  private readonly ambientInterval: number;
  private readonly activities = new Set<string>();
  private ambient = false;
  private dirty = false;
  private pending = false;
  private lastRender = Number.NEGATIVE_INFINITY;
  private lastTick = Number.NEGATIVE_INFINITY;
  /** Estimated display refresh interval (16.7 ms at 60 Hz, 8.3 ms at 120 Hz). */
  private refreshMs = 1000 / 60;
  private renderedFrames = 0;

  constructor(opts: FrameSchedulerOptions) {
    this.requestFrame = opts.requestFrame;
    this.renderFrame = opts.render;
    this.gpuBusy = opts.gpuBusy ?? (() => false);
    this.interactiveInterval = 1000 / (opts.interactiveFps ?? 60);
    this.ambientInterval = 1000 / (opts.ambientFps ?? 30);
  }

  /** Total frames drawn so far; it must not grow while nothing changes. */
  get frames(): number {
    return this.renderedFrames;
  }

  get mode(): FrameMode {
    if (this.dirty || this.activities.size > 0) return 'interactive';
    return this.ambient ? 'ambient' : 'idle';
  }

  /** Something changed once (a resize, a toggle): draw one more frame. */
  invalidate(): void {
    this.dirty = true;
    this.schedule();
  }

  /** Starts or stops a named interactive activity, such as a gesture or a camera tween. */
  setActive(name: string, active: boolean): void {
    if (active) this.activities.add(name);
    else this.activities.delete(name);
    if (active) this.schedule();
  }

  /** Turns the ambient animations (vehicles) on or off. */
  setAmbient(active: boolean): void {
    this.ambient = active;
    if (active) this.schedule();
  }

  private schedule(): void {
    if (this.pending || this.mode === 'idle') return;
    this.pending = true;
    this.requestFrame((time) => {
      this.onFrame(time);
    });
  }

  private onFrame(time: number): void {
    this.pending = false;
    const sinceTick = time - this.lastTick;
    this.lastTick = time;
    if (sinceTick > 0 && sinceTick < MAX_REFRESH_MS) this.refreshMs += (sinceTick - this.refreshMs) * 0.2;
    const mode = this.mode;
    if (mode === 'idle') return;
    const interval = mode === 'interactive' ? this.interactiveInterval : this.ambientInterval;
    const elapsed = time - this.lastRender;
    // Half a refresh of tolerance: frames land on the display's beat, so a 30 fps cap gives
    // every second refresh at 60 Hz and every fourth at 120 Hz, never an uneven 20 or 40.
    if (elapsed >= interval - this.refreshMs / 2 && !this.gpuBusy()) {
      this.dirty = false;
      const delta = Number.isFinite(elapsed) ? elapsed : 0;
      this.lastRender = time;
      this.renderedFrames++;
      this.renderFrame(time, delta);
    }
    this.schedule();
  }
}
